import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import subscriptionApi from '@/api/subscriptionApi';

interface SubscriptionPaymentFormProps {
    onSetupComplete: () => void;
    customerId: string;
    clientSecret: string;
}

const SubscriptionPaymentForm: React.FC<SubscriptionPaymentFormProps> = ({
    onSetupComplete,
    customerId,
    clientSecret
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);
        setError(null);
        setSuccess(false);


        try {
            // First submit the elements
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setError(submitError.message || 'An error occurred during submission.');
                return;
            }

            // Then confirm the setup
            const { error: setupError, setupIntent } = await stripe.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: window.location.href
                },
                redirect: "if_required"
            });

            if (setupError) {
                setError(setupError.message || 'An error occurred during setup.');
            } else if (setupIntent?.status === 'succeeded') {
                await subscriptionApi.setupStripeCustomerAfterPayment({
                    customerId,
                    paymentMethodId: setupIntent?.payment_method,
                });
                setSuccess(true);
                setTimeout(() => {
                    onSetupComplete();
                }, 2000);
            } else {
                setError('Setup did not complete successfully.');
            }
        } catch (err) {
            setError('An unexpected error occurred.');
            console.error('Setup error:', err);
        } finally {
            setProcessing(false);
        }
    };

    if (success) {
        return (
            <div className="text-center p-6">
                <div className="text-green-600 text-xl mb-4">✓</div>
                <h3 className="text-xl font-bold mb-2">Payment Method Set Up Successfully</h3>
                <p className="text-gray-600">Your payment method has been saved for future charges.</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                <PaymentElement />
            </div>

            {error && (
                <div className="text-red-600 text-sm">
                    {error}
                </div>
            )}

            <button
                type="submit"
                disabled={!stripe || processing}
                className={`w-full py-2 px-4 rounded-md text-white font-medium
                    ${processing || !stripe
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
            >
                {processing ? 'Processing...' : 'Set up payment method'}
            </button>
        </form>
    );
};

export default SubscriptionPaymentForm; 